<template>
  <div v-if="isLoading && route.name !== 'ProductDetails'" class="app-loader">
    <div class="app-loader-content">
      <div class="logo-container">
        <img src="@/assets/images/LOGO.svg" alt="KAME SEEDS" class="loader-logo">
        <div class="loader-spinner">
          <div class="spinner"></div>
        </div>
      </div>
    </div>
  </div>
  <AgeVerification />
  <NavbarComponent v-if="!isPanelShellRoute" />
  <div :class="{ 'panel-route': isPanelShellRoute, 'main-content': !isPanelShellRoute }">
    <router-view />
  </div>
  <FooterComponent v-if="!isPanelShellRoute" />
  <UnsavedChangesModal />
</template>
<script setup>
import AgeVerification from '@/components/AgeVerification.vue'
import NavbarComponent from '@/components/NavbarComponent.vue'
import FooterComponent from '@/components/FooterComponent.vue'
import UnsavedChangesModal from '@/components/UnsavedChangesModal.vue'
import '@/assets/css/variables.css'
import { onMounted, computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { pendingRequests, pendingMedia } from '@/services/apiClient';

const route = useRoute();
const isLoading = ref(true);

/**
 * El loader inicial NO se oculta por tiempo fijo: espera a que el contenido
 * visible esté realmente cargado (requests de la API + imágenes visibles, incluidas
 * las de `background-image` como el hero). Antes se ocultaba a los 1500ms y se
 * destapaba la página vacía: el contenido aparecía después.
 */
const SPLASH_MIN_MS = 1500; // mínimo en pantalla (evita el parpadeo del loader)
const SPLASH_MAX_MS = 10000; // tope duro: nunca dejar la app colgada
const CHECK_MS = 120; // cada cuánto se revisa
const SETTLE_MS = 240; // margen extra para que el browser pinte lo cargado

/** Elementos que pueden traer una imagen por CSS (el hero, franjas, banners…). */
const BG_CANDIDATES =
  '[class*="hero"], [class*="bg"], [class*="cover"], [class*="banner"], [class*="fondo"], [class*="page"], [class*="section"]';

/** url -> 'pending' | 'ok': preloader de las imágenes de fondo, para poder
 *  esperarlas (document.images NO las incluye y son las que "aparecen después"). */
const bgLoads = new Map();

function bgPendiente(url) {
  if (bgLoads.get(url) === 'ok') return false;
  if (!bgLoads.has(url)) {
    bgLoads.set(url, 'pending');
    const img = new Image();
    const listo = () => bgLoads.set(url, 'ok'); // onerror también: no bloquear
    img.onload = listo;
    img.onerror = listo;
    img.src = url;
  }
  return true;
}

/** Imágenes que el usuario ya está viendo y todavía no terminaron de cargar. */
function mediaPendiente() {
  const h = window.innerHeight;
  const visible = (r) => r.top < h * 1.1 && r.bottom > -h * 0.1;
  const faltan = [];

  Array.from(document.images).forEach((img) => {
    if (!img.complete && visible(img.getBoundingClientRect())) faltan.push(img.currentSrc || img.src);
  });

  document.querySelectorAll(BG_CANDIDATES).forEach((el) => {
    if (!visible(el.getBoundingClientRect())) return;
    const found = /url\(["']?([^"')]+)["']?\)/.exec(getComputedStyle(el).backgroundImage || '');
    if (!found || found[1].startsWith('data:')) return;
    if (bgPendiente(found[1])) faltan.push(found[1]);
  });

  return faltan;
}

/** Ocupado mientras haya API en vuelo o imágenes visibles cargando. */
function contenidoPendiente() {
  return pendingRequests.count > 0 || pendingMedia.count > 0 || mediaPendiente().length > 0;
}

/** Resuelve cuando el contenido visible está listo (con mínimo, tope y settle). */
function esperarContenido() {
  const inicio = Date.now();
  let quieto = 0;
  return new Promise((resolve) => {
    const revisar = () => {
      const transcurrido = Date.now() - inicio;
      if (transcurrido >= SPLASH_MAX_MS) return resolve();
      const ocupado = transcurrido < SPLASH_MIN_MS || contenidoPendiente();
      if (ocupado) {
        quieto = 0;
      } else {
        quieto += CHECK_MS;
        if (quieto >= SETTLE_MS) return resolve();
      }
      setTimeout(revisar, CHECK_MS);
    };
    revisar();
  });
}
const isPanelShellRoute = computed(() =>
  route.path.startsWith('/panel') ||
  route.path.startsWith('/ingreso') ||
  route.path.startsWith('/activar-cuenta')
)

onMounted(() => {
  // Verificar si hay datos en caché del formato antiguo y limpiarlos
  const oldCacheKeys = [
    'products',
    'productsTimestamp',
    'userOrders',
    'adminOrders',
    'ordersTimestamp'
  ];

  const hasOldCache = oldCacheKeys.some(key => {
    const value = localStorage.getItem(key);
    try {
      if (value) JSON.parse(value);
      return true;
    } catch {
      return false;
    }
  });

  if (hasOldCache && !localStorage.getItem('cacheVersion')) {
    console.log('Detectado caché antiguo, limpiando...');
    oldCacheKeys.forEach(key => localStorage.removeItem(key));
  }

  // Se destapa la página recién cuando el contenido visible terminó de cargar
  esperarContenido().then(() => {
    isLoading.value = false;
  });
});
</script>

<style>
@font-face {
  font-family: 'Adumu';
  src: url('@/assets/fonts/Adumu.ttf') format('truetype');
  font-weight: normal;
}
@font-face {
  font-family: 'DIIN';
  src: url('@/assets/fonts/DINNTEXT.otf') format('opentype');
  font-weight: normal;
}
.adumu-font{
  font-family: 'Adumu', sans-serif;
}
.dinn-font{
  font-family: 'DIIN', sans-serif;
}
  body {
    background-color: var(--black-color);
    color: var(--white-color);
    font-family: 'Adumu', sans-serif;
    letter-spacing: 0.07em;
  }
  
  /* Panel: Poppins (iconos FA excluidos en panel.css) */
  .panel-route {
    font-family: 'Poppins', sans-serif;
    letter-spacing: normal;
  }
  
  /* Añadir espacio para el contenido debajo de la navbar fija */
  .main-content {
    padding-top: 60px; /* Espacio para la barra de navegación */
  }

  @media (max-width: 767.98px) {
    .main-content {
      padding-top: 120px; /* Espacio para la barra de navegación en móvil */
    }
  }

  /* Loader principal */
  .app-loader {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: var(--black-color);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
  }
  
  .app-loader-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
  
  .logo-container {
    position: relative;
    width: 180px;
    height: 180px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  
  .loader-logo {
    width: 150px;
    height: auto;
    position: relative;
    z-index: 1;
    animation: pulse 2s infinite;
  }
  
  .loader-spinner {
    position: absolute;
    width: 170px;
    height: 170px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 0;
  }
  
  .spinner {
    border: 4px solid rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    border-top: 4px solid var(--primary-color);
    width: 100%;
    height: 100%;
    animation: spin 1.5s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  @keyframes pulse {
    0% { transform: scale(1); opacity: 0.8; }
    50% { transform: scale(1.1); opacity: 1; }
    100% { transform: scale(1); opacity: 0.8; }
  }
</style>
